"use client";

import useMediaQuery from "@repo/shared-ui/useMediaQuery";
import SwiperButton from "@repo/shared-ui/SwiperButton";
import React, { useRef, useState } from "react";
import "swiper/css";
import "swiper/css/navigation";
import { A11y, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import StockCardSkeleton from "../ClpStockCards/StockCardSkeleton";
import StockListNotFound from "../ClpStockCards/StockListNotFound";
import { PayLaterMtfCard } from "./PayLaterMtfCard";
import { PayLaterMtfStockItem } from "./PayLatermtfTypes";

const SKELETON_CARD_COUNT = 6;

interface PayLaterMtfGridProps {
  stocks: PayLaterMtfStockItem[];
  defaultImage?: string;
  errorMessage?: string;
  stockNotFoundImgUrl?: string;
  modalTitle?: string;
  modalBtnCta?: string;
  successMsg?: string;
  errorMsg?: string;
  nextArrowClassName?: string;
  isLoading?: boolean;
  payOnly?: string;
  onCardClick?: (stock: PayLaterMtfStockItem) => void;
}

const PayLaterMtfGridSkeletonRow = () => (
  <div className="relative flex overflow-hidden">
    {Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
      <div
        key={"PayLaterMtfGridSkeleton-" + index}
        className={`min-w-[156px] max-w-[156px] md:min-w-[214px] md:max-w-[214px] ${
          index === SKELETON_CARD_COUNT - 1 ? "mr-1" : "mr-3"
        }`}
      >
        <StockCardSkeleton />
      </div>
    ))}
  </div>
);

export const PayLaterMtfGrid: React.FC<PayLaterMtfGridProps> = ({
  stocks,
  defaultImage,
  errorMessage,
  stockNotFoundImgUrl,
  modalTitle,
  modalBtnCta,
  successMsg,
  errorMsg,
  nextArrowClassName,
  payOnly,
  isLoading = false,
  onCardClick,
}) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [end, setEnd] = useState(false);
  const [start, setStart] = useState(true);

  let content;
  if (isLoading) {
    content = <PayLaterMtfGridSkeletonRow />;
  } else if (!stocks || stocks.length <= 0) {
    content = (
      <StockListNotFound
        errorMessage={errorMessage ?? ""}
        stockNotFoundImgUrl={stockNotFoundImgUrl ?? ""}
      />
    );
  } else {
    content = (
      <div className="relative">
        {isMobile ? null : (
          <>
            <SwiperButton ref={prevRef} direction="prev" isDisabled={start} />
            <SwiperButton
              ref={nextRef}
              direction="next"
              isDisabled={end}
              className={nextArrowClassName}
            />
          </>
        )}

        <Swiper
          modules={[Navigation, Pagination, A11y]}
          spaceBetween={0}
          slidesPerView="auto"
          allowTouchMove
          observer
          observeParents
          navigation={
            isMobile
              ? false
              : {
                  prevEl: prevRef.current,
                  nextEl: nextRef.current,
                }
          }
          onBeforeInit={(swiper: any) => {
            if (isMobile) return;
            if (typeof swiper.params.navigation !== "boolean") {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }
          }}
          onInit={(swiper: any) => {
            setStart(swiper.isBeginning);
            setEnd(swiper.isEnd);
          }}
          onSlideChange={(swiper: any) => {
            setStart(swiper.isBeginning);
            setEnd(swiper.isEnd);
          }}
          onReachEnd={() => setEnd(true)}
          onFromEdge={() => setEnd(false)}
        >
          {stocks.map((stock, index) => (
            <SwiperSlide
              key={"PayLaterMtfGrid-" + index}
              className={`min-w-[156px]! max-w-[156px] md:min-w-[214px]! md:max-w-[214px] ${
                index === stocks.length - 1 ? "mr-1!" : "mr-3!"
              }`}
            >
              <PayLaterMtfCard
                stock={stock}
                defaultImage={defaultImage}
                modalTitle={modalTitle}
                modalBtnCta={modalBtnCta}
                successMsg={successMsg}
                errorMsg={errorMsg}
                payOnly={payOnly}
                onCardClick={onCardClick}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }

  return <div className="relative">{content}</div>;
};
